import React from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getIncentives } from 'apis/CommunityAPI';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';
import { CommunityIncentiveType } from 'types/incentives';
import useDataList from 'utils/hooks/useDataList';

import { CommunityIncentivesContext } from './CommunityIncentivesContext';
import CommunityIncentivesControls from './CommunityIncentivesControls';
import CommunityIncentivesModal from './CommunityIncentivesModal';
import CommunityIncentivesTable from './CommunityIncentivesTable';

const Container = styled.div`
  width: 100%;
  overflow: auto;
`;

const Content = styled.div`
  overflow: auto;
  .ant-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    font-family: var(--font-bold);
  }
  .ant-tabs-tab-btn {
    color: var(--text-secondary);
  }
`;

type Props = {
  communityInfo: CommunityType | undefined;
  ownershipType: number;
};

const CommunityIncentives = ({ communityInfo, ownershipType }: Props) => {
  const { contextCompany } = useAppSelector((state) => state.global);
  const [modalType, setModalType] = React.useState<string | undefined>(undefined);
  const [selectedIncentive, setSelectedIncentive] = React.useState<CommunityIncentiveType | undefined>(undefined);

  const { filters, setFilters } = useDataList({
    initialState: {
      filters: {
        careTypes: [],
      },
    },
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['incentives', communityInfo?.id, filters, contextCompany],
    queryFn: () =>
      getIncentives(communityInfo?.id || -1, {
        status: 1,
        competitors: [communityInfo?.id],
        care_types: filters.careTypes,
      }),
    enabled: Boolean(communityInfo?.id),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return (
    <CommunityIncentivesContext.Provider
      value={{
        communityId: communityInfo?.id || -1,
        data,
        isLoading,
        isFetching,
        filters,
        modalType,
        selectedIncentive,
        setSelectedIncentive,
        setFilters,
        setModalType,
        refetch,
      }}
    >
      {modalType && (
        <CommunityIncentivesModal
          incentive={selectedIncentive}
          openModal={Boolean(modalType)}
          community={communityInfo}
          modalType={modalType}
          ownershipType={ownershipType}
          onClose={() => {
            setModalType(undefined);
            setSelectedIncentive(undefined);
          }}
        />
      )}
      <Container>
        <Content>
          <CommunityIncentivesControls />
          <CommunityIncentivesTable />
        </Content>
      </Container>
    </CommunityIncentivesContext.Provider>
  );
};

export default CommunityIncentives;
